// decision-auto-delegated-answer-reconcile.ts — V2-1 review M1.
//
// Đường auto ghi journal (`decision_auto_applied`) TRƯỚC câu trả lời
// `delegated:<G0 id>` trong interview.json. Tiến trình chết giữa hai bước ⇒
// journal nói "đã áp" mà gate `interview_unanswered` vẫn chặn ticket.
// `reconcileDelegatedAnswersFromJournal` chạy ở `startSupervisor` (mỗi `run`,
// trước mọi dispatch) và ghi bù đúng answer đó — idempotent theo
// `question_id`: câu đã có answer (của người hay máy) không bị đụng.
import { join } from "node:path";
import { delegatedActor } from "./decision-policy-journal-events.js";
import { readInterview, recordInterviewAnswer } from "./interview-store.js";
import { scanJournal } from "./journal-scan-report.js";
import { MISSION_JOURNAL_FILE_NAME } from "./mission-store.js";

export async function recordDelegatedAnswer(
  controlRoot: string,
  questionId: string,
  optionId: string,
  g0RequestId: string,
  now: Date,
): Promise<void> {
  const recorded = await recordInterviewAnswer({
    controlRoot,
    questionId,
    answer: optionId,
    answeredBy: delegatedActor(g0RequestId),
    now: () => now,
  });
  // Không thể xảy ra với câu vừa ghi (id đã validate) — nếu có thì là lỗi thật, nói rõ.
  if (!recorded.ok) throw new Error(`ghi answer tự áp "${questionId}" lỗi: ${recorded.reason}`);
}

/** Số answer đã ghi bù. Câu không còn trong interview.json (đã bị thay) ⇒ bỏ qua. */
export async function reconcileDelegatedAnswersFromJournal(
  controlRoot: string,
  now: () => Date = () => new Date(),
): Promise<number> {
  const interview = await readInterview(controlRoot);
  if (interview === undefined) return 0;
  const scan = await scanJournal(join(controlRoot, MISSION_JOURNAL_FILE_NAME));
  let written = 0;
  for (const { delta } of scan.records) {
    if (delta.kind !== "decision_auto_applied") continue;
    const { question_id: qid, option_id: optionId, authority_ref: g0 } = delta;
    if (typeof qid !== "string" || typeof optionId !== "string" || typeof g0 !== "string") continue;
    if (interview.answers[qid] !== undefined) continue;
    if (!interview.questions.some((q) => q.id === qid)) continue;
    await recordDelegatedAnswer(controlRoot, qid, optionId, g0, now());
    written += 1;
  }
  return written;
}
